import PaginatorComponent, { Props as PaginatorProps } from '../paginator/paginator.component';
import { ReactElement, useMemo } from 'react';
import { SelectedFacet, Sort } from '../facets/interfaces';
import { maxPaginationSiblings, pageSize } from '@spa/lib/const';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import SelectedFacetsComponent from '@spa/components/selected-facets/selected-facets.component';
import SortsComponent from '../sorts/sorts.component';
import classNames from 'clsx';
import styles from './product-list-pagination.component.module.scss';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import useRuntimeSearchParams from '@spa/lib/react/hooks/useRuntimeSearchParams';

export interface Props {
  readonly id: string;
  readonly title?: string | null;
  readonly subTitle?: string | null;
  readonly selectedSortCode: string;
  readonly sorts: ReadonlyArray<Sort>;
  readonly totalCount: number;
  readonly paginationLimit?: number;
  readonly selectedFacets: ReadonlyArray<SelectedFacet>;
  readonly showTotalCountAndSort?: boolean;
}

const ProductListPagination: React.FC<Props> = ({
  id,
  title,
  subTitle,
  selectedSortCode,
  sorts,
  totalCount,
  paginationLimit,
  selectedFacets,
  showTotalCountAndSort = true
}) => {
  const { formatMessage }: NamespacedIntl = useNamespacedIntl('product-list');
  const searchParams: URLSearchParams = useRuntimeSearchParams();
  const pageParamStr: string | null = searchParams.get('page');
  const pageParam: number = pageParamStr ? Number.parseInt(pageParamStr, 10) : 1;

  const paginationProps: PaginatorProps = {
    id,
    pageSize,
    totalCount,
    siblingCount: maxPaginationSiblings,
    currentPage: pageParam,
    paginationLimit
  };

  const sortsComponent = useMemo((): ReactElement => {
    return <SortsComponent id={`${id}-sorts`} sorts={sorts} selectedSortCode={selectedSortCode} />;
  }, [id, selectedSortCode, sorts]);

  const showPaginator = totalCount / pageSize > 1;
  const showPaginationWrapper = showPaginator && totalCount > 0 && !showTotalCountAndSort;

  const getPaginationSummary = (): string => {
    const from: number = (pageParam - 1) * pageSize + 1;
    const to: number = Math.min(pageParam * pageSize, totalCount);
    const counterText: string = formatMessage('articleCount', {
      from,
      to,
      count: totalCount
    });

    return `${counterText}`;
  };

  return (
    <>
      {(title || subTitle) && showTotalCountAndSort && (
        <div className={styles['product-list-pagination__title-wrapper']}>
          {title && (
            <h1 className={classNames('h2', styles['product-list-pagination__headline'])}>
              {title}
            </h1>
          )}
          {subTitle && <p className={styles['product-list-pagination__sub-title']}>{subTitle}</p>}
          {showTotalCountAndSort && (
            <div className={styles['product-list-pagination__filters-container']}>
              <p
                className={styles['product-list-pagination__total-count']}
                data-track-id="resultCountContainer"
              >
                {getPaginationSummary()}
              </p>

              <div className={styles['product-list-pagination__facets-wrapper']}>
                <SelectedFacetsComponent selectedFacets={selectedFacets} />
                <div className={styles['product-list-pagination__sorts-wrapper']}>
                  {sortsComponent}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {showPaginationWrapper && !showTotalCountAndSort && (
        <div
          className={classNames({
            [styles['product-list-pagination__paginator-wrapper']]: true
          })}
        >
          <PaginatorComponent {...paginationProps} />
        </div>
      )}
    </>
  );
};

export default ProductListPagination;
